import { useState } from 'react';
import { Lock, Waves } from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { SectionSkeleton } from '../common/SectionSkeleton';
import { ExpensesSectionLabel } from '../expenses/ExpensesSectionLabel';
import { IncomeSourceIcon } from './IncomeSourceIcon';
import { aggregateReceivedBySource } from '../../lib/incomeCycleUtils';
import { resolveIncomeSourceColor } from '../../utils/incomeSourceIcons';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';

const PREVIEW_SOURCE_LIMIT = 4;

/** `.pen` → `ds/card/income-source` › `track`: barra 8px preenchida com a
 *  cor da própria fonte (sem tramas — fixo e variável distinguem-se no badge). */
function SourceProgressBar({ color, pct }) {
  const width = Math.round(Math.min(100, Math.max(0, pct)));

  return (
    <div
      role="progressbar"
      aria-valuenow={width}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-2 w-full overflow-hidden rounded-ds-full bg-track"
    >
      <div
        className="h-full rounded-ds-full transition-[width]"
        style={{ width: `${width}%`, backgroundColor: color }}
      />
    </div>
  );
}

/** `.pen` → `ds/card/income-source` › `badge`. */
function RecurrenceBadge({ isFixed }) {
  const Icon = isFixed ? Lock : Waves;

  return (
    <span
      className={cn(
        'type-chip inline-flex w-fit items-center gap-space-1 rounded-ds-full border px-2 py-0.5 font-bold',
        isFixed
          ? 'border-sage-border bg-sage-tint text-sage-on-tint'
          : // `$warning-tint` é `#B4530933` em dark — alfa via modificador `/20`.
            'border-border bg-warning-tint text-warning-fg dark:bg-warning-tint/20',
      )}
    >
      <Icon className="h-2.5 w-2.5 shrink-0" aria-hidden />
      {isFixed ? pt.income.breakdown.badgeFixed : pt.income.breakdown.badgeVariable}
    </span>
  );
}

export function IncomeSourcesBreakdown({
  summary,
  sources = [],
  isLoading = false,
  className,
  title = pt.income.breakdown.title,
  subtitle = pt.income.breakdown.subtitle,
}) {
  const [showAll, setShowAll] = useState(false);
  const received = Number(summary?.income?.total || 0);
  const rows = aggregateReceivedBySource(
    summary?.income?.by_source || [],
    sources,
    received,
  );

  if (isLoading) {
    return <SectionSkeleton variant="list" className={className} />;
  }

  if (!rows.length) {
    return null;
  }

  const visibleRows = showAll ? rows : rows.slice(0, PREVIEW_SOURCE_LIMIT);
  const hasMoreSources = rows.length > PREVIEW_SOURCE_LIMIT;

  return (
    <div className={cn('flex min-w-0 flex-col gap-space-5', className)}>
      <ExpensesSectionLabel title={title} hint={subtitle} />

      <ul className="flex min-w-0 flex-col gap-space-2-5">
        {visibleRows.map((row) => {
          const source = {
            category: row.category,
            color: row.color,
            icon: row.icon,
            name: row.name,
          };

          return (
            <li
              key={row.sourceId}
              data-ds-id="ds/card/income-source"
              className="flex min-w-0 flex-col gap-space-2-5 rounded-ds-lg border border-border bg-card px-space-4 py-space-3 shadow-[0_6px_16px] shadow-glass-shadow/[0.12] dark:shadow-glass-shadow/[0.45]"
            >
              <div className="flex min-w-0 items-center gap-space-3">
                <IncomeSourceIcon
                  source={source}
                  size="lg"
                  background="muted"
                  className="rounded-ds-md"
                />

                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <p className="type-body truncate font-semibold text-card-foreground">
                    {row.name}
                  </p>
                  <RecurrenceBadge isFixed={row.isFixed} />
                </div>

                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  <p className="type-display-row tnum text-card-foreground">
                    {formatEUR(row.amount)}
                  </p>
                  <p className="type-caption tnum text-muted-foreground">
                    {pt.income.breakdown.pctOfCycle.replace(
                      '{pct}',
                      String(Math.round(row.pctOfTotal)),
                    )}
                  </p>
                </div>
              </div>

              <SourceProgressBar
                color={resolveIncomeSourceColor(source)}
                pct={row.pctOfTotal}
              />
            </li>
          );
        })}
      </ul>

      {!showAll && hasMoreSources ? (
        <div className="flex justify-center pt-space-1">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="type-link-sm text-sage-on-tint hover:underline"
          >
            {pt.income.breakdown.seeAll}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default IncomeSourcesBreakdown;
